import React, { useState, useEffect } from "react";
import { styled, useTheme } from "@mui/material/styles";
import {
  Box,
  Drawer as MuiDrawer,
  AppBar as MuiAppBar,
  Toolbar,
  IconButton,
  Divider,
  List,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import MenuBookRounded from "@mui/icons-material/MenuBookRounded";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DashboardRounded from "@mui/icons-material/DashboardRounded";
import NavListItem from "components/common/NavListItem";
import { useNavigate, useLocation } from "react-router-dom";
import Logo from "components/styled/Logo";
import StatusBanner from "components/dashboard/StatusBanner";
import { useAuth } from "hooks/useAuth";
import { useFdeRole } from "hooks/useFdeRole";
import { useGlobalTciManager } from "hooks/useGlobalTciManager";
import { DrawerContext } from "context/DrawerContext";
import {
  OnboardingDrawer,
  PatientDrawer,
  ProfessionalDrawer,
  FacilityDrawer,
  SettingsDrawer,
  AdminDrawer,
  ChartMindManagerDrawer,
  TciManagerDrawer,
  GlobalAdminDrawer,
  GlobalTciManagerDrawer,
  RegionalAdminDrawer,
  MasterChartDrawer,
  MarketingExecutiveDrawer,
  BtybMarketerDrawer,
} from "./DrawerComponents";
import { isPatientOrP2LikeRole } from "shared/utils/constants/p2LikeRoles";
import { isDevAuthFirestoreUser } from "utils/isDevAuthFirestoreUser";
import {
  USERS_ROLE_BTYB_MARKETER,
  USERS_ROLE_MARKETING_EXECUTIVE,
} from "constants/userOrgRoles";
import { isMasterChartDashboardPath } from "constants/masterChartRoutes";
import { APP_TOP_BAR_HEIGHT_PX } from "layout/appTopBarLayout";
import { normalizePartnerTier } from "constants/partnerTiers";

const drawerWidth = 300;
const MOBILE_DASHBOARD_APP_BAR_PX = 56;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  boxShadow: "none",
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
  ...(!open && {
    width: `calc(100% - ${theme.spacing(7)} - 1px)`,
    [theme.breakpoints.up("sm")]: {
      width: `calc(100% - ${theme.spacing(8)} - 1px)`,
    },
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  "& .MuiDrawer-paper": {
    backgroundColor: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.divider}`,
    ...(open ? openedMixin(theme) : closedMixin(theme)),
  },
  ...(open ? openedMixin(theme) : closedMixin(theme)),
}));

const DashboardNav = ({ children }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));
  const {
    user,
    subscription,
    userData,
    activeSubscriptionRole,
    logout,
    isGlobalAdmin,
    isRegionalAdmin,
    isBetaAdmin,
    canManageZipSlotAds,
    organization,
    tokenClaims,
  } = useAuth();
  const { isFdeDc } = useFdeRole();
  const { isGlobalTciManager } = useGlobalTciManager();
  const navigate = useNavigate();
  const location = useLocation();
  const onMasterChart = isMasterChartDashboardPath(location.pathname);
  const showFoundingPartnerAccounting =
    userData?.role === "admin" &&
    normalizePartnerTier(organization?.partnerTier) === "founding";
  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [isClosing, setIsClosing] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const handleDrawerClose = () => {
    setIsClosing(true);
    setMobileOpen(false);
  };

  const handleDrawerTransitionEnd = () => {
    setIsClosing(false);
  };

  const handleDrawerToggle = () => {
    if (!isClosing) {
      setMobileOpen(!mobileOpen);
    }
  };

  const handleDesktopDrawerOpen = () => setDrawerOpen(true);
  const handleDesktopDrawerClose = () => setDrawerOpen(false);

  const drawerExpanded = isDesktop ? drawerOpen : mobileOpen;

  const hideCareMapForBeta = Boolean(userData?.betaAccess);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  useEffect(() => {
    const handleScroll = (e) => {
      const scrollTop = e.target.scrollTop;
      setIsScrolled(scrollTop > 10);
    };

    const mainContent = document.querySelector("[data-main-content]");
    if (mainContent) {
      mainContent.addEventListener("scroll", handleScroll);
      return () => mainContent.removeEventListener("scroll", handleScroll);
    }
  }, []);

  // User is verified if email verified OR phone verified OR synthetic @dev.local
  // (client SDK cannot set emailVerified on dev-auth accounts).
  const isVerified =
    user?.emailVerified ||
    userData?.phoneVerified ||
    isDevAuthFirestoreUser(userData);

  const adminDashboardHome =
    isGlobalAdmin || isRegionalAdmin || userData?.role === "admin"
      ? "/dashboard/prompts"
      : "/dashboard";

  const drawer = (
    <DrawerContext.Provider value={drawerExpanded}>
      <DrawerHeader
        sx={{
          justifyContent: drawerOpen ? "space-between" : "flex-end",
          px: 2,
        }}
      >
        {drawerOpen && (
          <Box sx={{ display: { xs: "none", sm: "block" } }}>
            <Logo />
          </Box>
        )}
        <IconButton
          aria-label={drawerOpen ? "collapse drawer" : "expand drawer"}
          onClick={
            isDesktop
              ? drawerOpen
                ? handleDesktopDrawerClose
                : handleDesktopDrawerOpen
              : handleDrawerToggle
          }
          sx={{
            ml: drawerOpen ? 0 : "auto",
            display: { xs: "none", sm: "inline-flex" },
          }}
        >
          {theme.direction === "rtl" ? (
            drawerOpen ? (
              <ChevronRightIcon />
            ) : (
              <ChevronLeftIcon />
            )
          ) : drawerOpen ? (
            <ChevronLeftIcon />
          ) : (
            <MenuIcon />
          )}
        </IconButton>
        <Box
          sx={{ display: { xs: "flex", sm: "none" }, alignItems: "center" }}
        >
          <IconButton
            aria-label="close drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Logo />
        </Box>
      </DrawerHeader>
      <Divider />
      <div onClick={isDesktop ? undefined : handleDrawerToggle}>
        {onMasterChart && !isPatientOrP2LikeRole(userData?.role) ? (
          <>
            <MasterChartDrawer />
            <SettingsDrawer
              activeSubscriptionRole={activeSubscriptionRole}
              userData={userData}
            />
            <List>
              <NavListItem
                icon={<DashboardRounded />}
                text="Back to dashboard"
                link={adminDashboardHome}
              />
            </List>
            <Divider />
            <OnboardingDrawer
              logout={handleLogout}
              hideCareMap={hideCareMapForBeta}
            />
          </>
        ) : isGlobalAdmin ? (
          <GlobalAdminDrawer
            onLogout={handleLogout}
            hideCareMap={hideCareMapForBeta}
          />
        ) : isRegionalAdmin ? (
          <RegionalAdminDrawer
            onLogout={handleLogout}
            hideCareMap={hideCareMapForBeta}
          />
        ) : isGlobalTciManager && userData?.role !== "admin" ? (
          <GlobalTciManagerDrawer
            onLogout={handleLogout}
            hideCareMap={hideCareMapForBeta}
          />
        ) : (
          <>
            {userData?.role === "facility" && isVerified && (
              <FacilityDrawer
                userData={userData}
                includeZipSlotAds={canManageZipSlotAds}
              />
            )}

            {userData?.role === USERS_ROLE_MARKETING_EXECUTIVE && isVerified && (
              <MarketingExecutiveDrawer />
            )}

            {userData?.role === USERS_ROLE_BTYB_MARKETER && isVerified && (
              <BtybMarketerDrawer />
            )}

            {isPatientOrP2LikeRole(userData?.role) && isVerified && (
              <PatientDrawer subscription={subscription} userData={userData} />
            )}

            {userData?.role === "professional" && isVerified && (
              <ProfessionalDrawer
                subscription={subscription}
                userData={userData}
                includeZipSlotAds={canManageZipSlotAds}
              />
            )}

            {userData?.role === "admin" && isVerified && (
              <ChartMindManagerDrawer
                isBetaAdmin={isBetaAdmin}
                showTciManager={isFdeDc || isGlobalAdmin || isGlobalTciManager}
                showGlobalTciManager={isGlobalTciManager && !isGlobalAdmin}
                showFoundingPartnerAccounting={showFoundingPartnerAccounting}
              />
            )}

            {isFdeDc && userData?.role !== "admin" && isVerified && (
              <TciManagerDrawer onLogout={handleLogout} hideCareMap={hideCareMapForBeta} />
            )}

            {isVerified ? (
              <>
                <AdminDrawer isGlobalAdmin={isGlobalAdmin} />
                {tokenClaims?.specialtyKbReviewer === true ? (
                  <List>
                    <NavListItem
                      icon={<MenuBookRounded />}
                      text="SpecialtyKB Editors"
                      secondary="Review your assigned specialty"
                      link="/specialty-kb/editors"
                    />
                  </List>
                ) : null}
                <SettingsDrawer
                  activeSubscriptionRole={activeSubscriptionRole}
                  userData={userData}
                />
              </>
            ) : null}

            <OnboardingDrawer
              logout={handleLogout}
              hideCareMap={hideCareMapForBeta}
            />
          </>
        )}
      </div>
    </DrawerContext.Provider>
  );

  const closedDrawerWidth = `calc(${theme.spacing(8)} + 1px)`;
  const mainWidth = isDesktop
    ? drawerOpen
      ? `calc(100% - ${drawerWidth}px)`
      : `calc(100% - ${closedDrawerWidth})`
    : "100%";

  return (
    <Box sx={{ display: "flex", flex: 1, minHeight: 0, bgcolor: "background.default" }}>
      {!isDesktop ? (
      <AppBar
        position="fixed"
        open={false}
        sx={{
          display: "block",
          boxShadow: "none",
          top: APP_TOP_BAR_HEIGHT_PX,
          transition: "box-shadow 0.3s ease-in-out",
          ...(isScrolled && {
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          }),
          width: "100%",
          marginLeft: 0,
          zIndex: (theme) => theme.zIndex.appBar,
        }}
      >
        <Toolbar sx={{ display: "flex" }}>
          <IconButton
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Box>
            <Logo />
          </Box>
        </Toolbar>
      </AppBar>
      ) : null}
      <Box
        component="nav"
        sx={{ flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <MuiDrawer
          variant="temporary"
          open={mobileOpen}
          onTransitionEnd={handleDrawerTransitionEnd}
          onClose={handleDrawerClose}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              backgroundColor: "background.paper",
              borderRight: 1,
              borderColor: "divider",
            },
          }}
        >
          {drawer}
        </MuiDrawer>
        <Drawer
          variant="permanent"
          open={drawerOpen}
          sx={{
            display: { xs: "none", sm: "block" },
          }}
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        data-main-content
        sx={{
          flexGrow: 1,
          margin: "auto",
          p: 3,
          paddingTop: {
            xs: `${MOBILE_DASHBOARD_APP_BAR_PX}px`,
            sm: 0,
          },
          paddingBottom: 0,
          paddingLeft: 0,
          paddingRight: 0,
          width: mainWidth,
          minHeight: 0,
          height: "100%",
          overflow: "auto",
          position: "relative",
          bgcolor: "background.default",
          boxSizing: "border-box",
        }}
      >
        <StatusBanner />
        {children}
      </Box>
    </Box>
  );
};

export default DashboardNav;
